import { z } from 'zod';
import { contentIdSchema, pathSchema } from './content-id.js';
import { abilityNameSchema } from '../content/common.js';
/**
 * Заклинания: ячейки, подготовленное и концентрация (D4).
 *
 * Всё это — свёртка журнала, как хиты и инвентарь: подготовил,
 * потратил ячейку, сосредоточился, отдохнул. Снимка нет нигде,
 * и «осталось ячеек» нигде не хранится — оно считается.
 *
 * Сколько ячеек и заклинаний положено, знает запись класса: таблица
 * уровней разобрана в данные (`levels[n].spellcasting`). В коде этих
 * чисел нет, иначе новый класс потребовал бы правки движка.
 */
/** Круг заклинания: девять в SRD 5.2, заговоры — нулевой. */
export const spellLevelSchema = z.number().int().min(1).max(9);
export const spellSlotSchema = z.strictObject({
    level: spellLevelSchema,
    total: z.number().int().nonnegative(),
    used: z.number().int().nonnegative(),
});
/** Заклинание так, как его показывает лист: карточка без расчётов. */
export const sheetSpellSchema = z.strictObject({
    id: contentIdSchema,
    name: z.string().min(1),
    /** Ноль — заговор. */
    level: z.number().int().min(0).max(9),
    school: z.string().min(1),
    castingTime: z.string().min(1),
    range: z.string().min(1),
    components: z.string().min(1),
    duration: z.string().min(1),
    concentration: z.boolean(),
    ritual: z.boolean(),
    /** Описание из записи — на языке источника. */
    text: z.string(),
    /**
     * Наш перевод описания; `null` — запись ещё не переводили (D32).
     * Оригинал приходит рядом и остаётся доступным: перевод —
     * переработка материала SRD, и подменять им источник нельзя
     * (`ATTRIBUTION.md`).
     */
    textRu: z.string().nullable(),
});
export const spellbookSchema = z.strictObject({
    /**
     * Класс умеет колдовать. У остальных вкладка объясняет, почему
     * пусто, а не показывает пустоту.
     */
    caster: z.boolean(),
    /**
     * Как класс называет свой список: «Prepared Spells» у волшебника,
     * «Spells Known» у колдуна. Слово из записи, а не из кода.
     */
    label: z.string().min(1).nullable(),
    /**
     * Характеристика заклинаний и пути величин, которые из неё
     * считаются: сложность спасброска и бонус атаки заклинаниями
     * (SRD 5.2, с. 23). Сами числа лежат в величинах листа
     * с провенансом — здесь только имена, чтобы вкладка знала,
     * что показывать.
     */
    ability: abilityNameSchema.nullable(),
    saveDcPath: pathSchema.nullable(),
    attackPath: pathSchema.nullable(),
    cantripsKnown: z.number().int().nonnegative(),
    preparedMax: z.number().int().nonnegative(),
    slots: z.array(spellSlotSchema),
    cantrips: z.array(sheetSpellSchema),
    prepared: z.array(sheetSpellSchema),
    /**
     * На чём сосредоточен. Новое заклинание с концентрацией снимает
     * прежнее: «you lose Concentration on a spell if you cast another
     * spell that requires Concentration» (SRD 5.2, с. 186).
     */
    concentration: contentIdSchema.nullable(),
    /**
     * Когда меняют набор подготовленных: длинный отдых или уровень
     * класса. `null` — класс не колдует. Нужно интерфейсу: сказать
     * об этом до нажатия дешевле, чем отказать после.
     */
    changeOn: z.enum(['long_rest', 'level']).nullable(),
});
/** Действие с заклинаниями — команда серверу (D3). */
export const spellActionSchema = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('prepare'), spell: contentIdSchema }),
    z.strictObject({ kind: z.literal('unprepare'), spell: contentIdSchema }),
    z.strictObject({ kind: z.literal('spend'), level: spellLevelSchema }),
    z.strictObject({ kind: z.literal('restore'), level: spellLevelSchema }),
    z.strictObject({
        kind: z.literal('concentrate'),
        /** `null` — сосредоточение снято. */
        spell: contentIdSchema.nullable(),
    }),
]);
export const spellProblemCodeSchema = z.enum([
    'unknown-spell',
    /** Запись есть, но это не заклинание. */
    'not-a-spell',
    /** Заклинания нет в списке класса. */
    'not-in-class-list',
    'already-prepared',
    'not-prepared',
    /** Больше, чем позволяет таблица класса. */
    'no-room',
    /** Ячеек этого круга у персонажа нет вовсе. */
    'no-such-slot',
    /** Все ячейки круга уже потрачены. */
    'no-slots-left',
    /** Восстанавливать нечего: ни одна ячейка круга не потрачена. */
    'nothing-to-restore',
    /** Класс не колдует. */
    'not-a-caster',
    /** Сосредоточиться можно на подготовленном заклинании с концентрацией. */
    'not-concentration',
    /**
     * Набор подготовленных меняют не когда угодно: у жреца, друида,
     * паладина, следопыта и волшебника — по окончании длинного отдыха,
     * у барда, чародея и колдуна — при получении уровня класса
     * («Changing Your Prepared Spells»).
     */
    'not-now',
    /** Заклинание за раз уже поменяли: до следующего окна больше нельзя. */
    'change-limit',
    /** Заговоры набора не меняют: они приходят с уровнем класса. */
    'cantrip-fixed',
]);
export const spellProblemSchema = z.strictObject({
    code: spellProblemCodeSchema,
    /** О чём речь: запись заклинания либо круг числом. */
    subject: z.string().nullable(),
    message: z.string().min(1),
});
//# sourceMappingURL=spells.js.map